import React from 'react'
import { useTranslation } from 'react-i18next'
import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Tab, Typography, useMediaQuery, useTheme } from '@mui/material'
import InfoBlock from '../../components/InfoBlock'
import TradingStatsOverview from './TradingStatsOverview'
import TradingStatsRights from './TradingStatsRights'
import { isShare } from '../../../utils/issues/sharesOrUnits'
import { calculateIssueTotalOfferingSize } from '../utils'
import { IssueSingleItem } from '@finance-lab/utils/types'

interface TradingStatsProps {
    issue: Pick<
        IssueSingleItem,
        | 'type'
        | 'totalOfferedUnits'
        | 'unitsReceived'
        | 'rightsEndDate'
        | 'rightsStartDate'
        | 'rightsRequired'
        | 'currency'
        | 'investors'
        | 'sources'
        | 'tradesStats'
        | 'unitInstrumentName'
        | 'unitInstrumentSymbol'
        | 'unitSubPrice'
        | 'sharesInUnit'
        | 'secondaryOffering'
    >
}

function TradingStats({ issue }: TradingStatsProps) {
    const { t } = useTranslation()
    const [selectedTab, setSelectedTab] = React.useState<'overview' | 'rights'>('overview')

    const theme = useTheme()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

    let content: React.ReactNode

    const { totalOfferingSize } = calculateIssueTotalOfferingSize(issue)

    if (issue.type === 'IPO') {
        if (issue.tradesStats?.length) {
            content = (
                <TradingStatsOverview
                    currency={issue.currency}
                    tradesStats={issue.tradesStats}
                    unitInstrumentSymbol={issue.unitInstrumentSymbol}
                />
            )
        } else {
            content = <Typography>{t('pages.issues.tradingStats.notStarted')}</Typography>
        }
    } else if (issue.type !== 'Rights issue') {
        content = (
            <TradingStatsOverview
                currency={issue.currency}
                tradesStats={issue.tradesStats}
                unitInstrumentSymbol={issue.unitInstrumentSymbol}
            />
        )
    } else {
        const rightsTradeStats = issue.tradesStats.find(({ periodType }) => periodType === 'rightsTradePeriod')
        content = (
            <TabContext value={selectedTab}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={(e, value) => setSelectedTab(value)}>
                        <Tab label={t('pages.issues.tradingStats.overview')} value='overview' />
                        <Tab label={t('pages.issues.tradingStats.rights')} value='rights' />
                    </TabList>
                </Box>
                <TabPanel value='overview' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                    <TradingStatsOverview
                        currency={issue.currency}
                        tradesStats={issue.tradesStats}
                        unitInstrumentSymbol={issue.unitInstrumentSymbol}
                        sharesInUnit={issue.sharesInUnit}
                    />
                </TabPanel>
                <TabPanel value='rights' sx={isSmallScreen ? { padding: '12px 0px' } : null}>
                    <TradingStatsRights
                        endDateQuantity={rightsTradeStats?.endDateQuantity || null}
                        endDateVwap={rightsTradeStats?.endDateVwap || null}
                        hasShares={isShare(issue)}
                        totalOfferedUnits={issue.totalOfferedUnits}
                        investors={issue.investors}
                        rightsEndDate={issue.rightsEndDate}
                        rightsStartDate={issue.rightsStartDate}
                        rightsRequired={issue.rightsRequired}
                        tradeQuantity={rightsTradeStats?.totalQuantity || null}
                        vwap={rightsTradeStats?.vwap || null}
                        unitsReceived={issue.unitsReceived}
                        unitSubPrice={issue.unitSubPrice}
                        totalOfferingSize={totalOfferingSize}
                    />
                </TabPanel>
            </TabContext>
        )
    }

    return <InfoBlock title={t('pages.issues.tradingStats.title')}>{content}</InfoBlock>
}

export default TradingStats
